import Big from 'big.js';
import {EMPTY, from, Subject} from 'rxjs';
import {expand, filter, map, pluck, switchMap, withLatestFrom} from 'rxjs/operators';
import {array2Map} from '@/helper/array';
import {max, min, numUtil} from '@/helper/num';
import {timeUtil} from '@/helper/time';
import {exportExcelFromAoaData} from '@/helper/xlsx';
import {downloadApi} from '@/api/download';
import {investmentApi} from '@/api/investment';
import {assetsApi} from '@/api/assets';
import {launchpoolApi} from '@/api/launch-pool';
import {marginTradeApi} from '@/api/margin-trade';
import {mc} from '@/core';
import {HistoryQueryUtaState} from '@/modules/assets/const';
import {INVESTMENT_TYPE, LAUNCHPOOL_TYPE, WALLET_TYPE, TRADE_TYPE} from '@/modules/assets/map';
import {genOrderDirection} from '@/modules/assets/util';
import {formatterUpgradedBeforeAfter} from '@/modules/assets/fund-details/util';
import {spots$} from '@/streams/config/spot';
import {contracts$} from '@/streams/config/contract';
import {currencies$} from '@/streams/config/currency';
import {AnnualInterestRatePrecision, HourlyInterestRatePrecision, InterestValuePrecision} from '@/streams/margin-trade';
import {convertOrdersHistory} from '@/modules/assets/contract-history/components/order-history/stream';
import {convertTradeHistory} from '@/modules/assets/contract-history/components/trade-history/stream';
import {convertAccountFunds} from '@/modules/assets/fund-details/stream';
import {convertClosePositionExport} from '@/modules/assets/contract-history/components/pnl-details/stream';
import {TypeMap as ContractRepayTypeMap} from '@/modules/assets/contract-history/components/repay-history/stream';
import {
  genMarginFundDetails,
  genMarginTradeExportList,
  loadMarginTradeHistory,
  loadFundAccountDetails,
  loadTradingAccountDetails,
  loadMarginTradeDetails,
  getCurrency,
  getSpot,
  createGenMarginTradeBorrowHistoryFun,
  createGenMarginTradeRepayHistoryFun,
  loadMarginTradeRepayHistory,
  loadMarginOrderHistory,
} from './margin-fund';
import lang from '@/modules/assets/spot-history/components/convert-history/ConvertHistoryPage.lang';

const LIMIT = 500;
const MAX_LIMIT = 2000;
const MAX_TYPE_LIST = ['ContractTrade', 'ContractOrder'];
const EVENT_TYPE_LIST = [1004, 1014, 1017];
const MAX_EXPORT_LIMIT = {
  MarginTradeFundsDetails: 100,
  MarginTradeInterestHistory: 100,
  MarginTradeBorrowHistory: 100,
  MarginTradeBorrowHistoryUpgraded: 100,
  MarginTradeRepayHistory: 100,
  MarginTradeRepayHistoryUpgraded: 100,
  MarginOrderHistory: 100,
  MarginTradeHistory: 100,
};

export const timeRangeInfo$ = new Subject();

const context$ = timeRangeInfo$.pipe(pluck('context'));

const loadHistoryMap = {
  SpotOrder: loadSpotOrderHistory,
  SpotOrderUpgraded: loadSpotOrderHistory,
  SpotTrade: loadSpotTradeHistory,
  SpotTradeUpgraded: loadSpotTradeHistory,
  ContractOrder: loadContractOrderHistory,
  ContractTrade: loadContractTradeHistory,
  WalletDetails: loadWalletDetails,
  Deposit: loadDeposit,
  Withdrawal: loadWithdraw,
  InvestmentAccount: loadInvestmentAccount,
  ClosedPNL: loadClosedPNL,
  ConversionHistory: loadConversionHistory,
  ContractTradeAccountsHistory: createLoadContractTradeAccountsHistory(0),
  ContractTradeAccountsUpgradedHistory: createLoadContractTradeAccountsHistory(1),
  ContractRepayHistory: loadContractRepayHistory,
  ContractBorrowHistory: loadContractBorrowHistory,
  ContractInterestHistory: loadContractInterestHistory,
  LaunchPoolHistory: loadLaunchPoolHistory,
  MarginTradeFundsDetails: loadMarginTradeDetails,
  MarginTradeInterestHistory: loadMarginTradeInterestHistory,
  MarginTradeBorrowHistory: loadMarginTradeBorrowHistory,
  MarginTradeBorrowHistoryUpgraded: loadMarginTradeBorrowHistory,
  MarginTradeRepayHistory: loadMarginTradeRepayHistory,
  MarginTradeRepayHistoryUpgraded: loadMarginTradeRepayHistory,
  MarginOrderHistory: loadMarginOrderHistory,
  MarginTradeHistory: loadMarginTradeHistory,
  TradingAccountHistory: loadTradingAccountDetails,
  FundAccountHistory: loadFundAccountDetails,
};

const genExportListMap = {
  SpotOrder: genSpotOrderExportList,
  SpotOrderUpgraded: createGenMarginOrderExportList(true),
  SpotTrade: genSpotTradeExportList,
  SpotTradeUpgraded: genMarginTradeExportList,
  ContractOrder: genContractOrderExportList,
  ContractTrade: genContractTradeExportList,
  WalletDetails: genWalletExportList,
  Deposit: genDepositExportList,
  Withdrawal: genWithdrawalExportList,
  InvestmentAccount: genInvestmentAccount,
  ClosedPNL: genClosedPNL,
  ConversionHistory: genConversionHistory,
  ContractTradeAccountsHistory: genContractTradeAccountsHistory,
  ContractTradeAccountsUpgradedHistory: genContractTradeAccountsHistory,
  ContractRepayHistory: genContractRepayHistory,
  ContractBorrowHistory: genContractBorrowHistory,
  ContractInterestHistory: genContractInterestHistory,
  LaunchPoolHistory: genLaunchPoolHistory,
  MarginTradeFundsDetails: genMarginFundDetails,
  MarginTradeInterestHistory: genMarginTradeInterestHistory,
  MarginTradeBorrowHistory: createGenMarginTradeBorrowHistoryFun(),
  MarginTradeBorrowHistoryUpgraded: createGenMarginTradeBorrowHistoryFun(true),
  MarginTradeRepayHistory: createGenMarginTradeRepayHistoryFun(),
  MarginTradeRepayHistoryUpgraded: createGenMarginTradeRepayHistoryFun(true),
  MarginOrderHistory: createGenMarginOrderExportList(),
  MarginTradeHistory: genMarginTradeExportList,
  TradingAccountHistory: genMarginFundDetails,
  FundAccountHistory: genMarginFundDetails,
};

timeRangeInfo$
  .pipe(
    map(({rangeType, formattedStartTime, formattedEndTime, context}) => {
      const {isPerpetualV2, type, currency, symbol, customizeQuery = {}} = context;
      const query = {
        ...customizeQuery,
        offset: 0,
        limit: MAX_EXPORT_LIMIT.hasOwnProperty(type) ? MAX_EXPORT_LIMIT[type] : MAX_TYPE_LIST.includes(type) ? MAX_LIMIT : LIMIT,
        ...convertTimeInfo(rangeType, formattedStartTime, formattedEndTime),
        currency,
        symbol,
      };
      return {query, rows: [], total: Infinity, type, isPerpetualV2};
    }),
    expand(({query, rows, total, type, isPerpetualV2}) => {
      if (rows.length >= total) {
        return EMPTY;
      }
      return from(loadHistoryMap[type](query, isPerpetualV2)).pipe(
        map((result) => {
          return {
            query: {
              ...query,
              offset: rows.length + result.rows.length,
            },
            rows: [...rows, ...result.rows],
            total: result.total,
            type,
          };
        })
      );
    }),
    filter((o) => o.rows.length >= o.total),
    withLatestFrom(spots$, contracts$, currencies$, context$),
    map(([historyData, spots, contracts, currencies, context]) => genExportListMap[context.type](historyData.rows, {spots, contracts, currencies, lang: context.lang, LG: context.LG})),
    filter(Boolean),
    withLatestFrom(context$),
    switchMap(downloadWorkBook)
  )
  .subscribe(() => {
    mc.send('download', 'disappear');
  });

function convertTimeInfo(rangeType, formattedStartTime, formattedEndTime) {
  return genTimeMap[rangeType](formattedStartTime, formattedEndTime);
}

const genTimeMap = {
  hours: gen24Hours,
  yesterday: genYesterday,
  weeks: genWeeks,
  monthDate: genMonthDate,
  pastMonth: genPastMonth,
  past3Month: genPast3Months,
  Custom: genCustom,
};

function gen24Hours() {
  const d = new Date();
  const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1, d.getUTCHours(), d.getMinutes(), d.getSeconds());
  return {start, end: Date.now(), dateType: 1};
}

function genYesterday() {
  const d = new Date();
  const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1);
  const end = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return {start, end, dateType: 2};
}

function genWeeks() {
  const d = new Date();
  const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 14, d.getUTCHours(), d.getMinutes(), d.getSeconds());
  return {start, end: Date.now(), dateType: 3};
}

function genMonthDate() {
  const d = new Date();
  const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  return {start, end: Date.now(), dateType: 4};
}

function genPastMonth() {
  const d = new Date();
  const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, d.getUTCDate(), d.getUTCHours(), d.getMinutes(), d.getSeconds());
  return {start, end: Date.now(), dateType: 5};
}

function genPast3Months() {
  const d = new Date();
  const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 3, d.getUTCDate(), d.getUTCHours(), d.getMinutes(), d.getSeconds());
  return {start, end: Date.now(), dateType: 6};
}

function genCustom(formattedStartTime, formattedEndTime) {
  const [Y, M, D] = formattedStartTime.split('-').map((o) => Number(o));
  const start = Date.UTC(Y, M - 1, D);
  const [y, m, d] = formattedEndTime.split('-').map((o) => Number(o));
  const end = Date.UTC(y, m - 1, d + 1);
  return {start, end, dateType: 7};
}

async function loadSpotTradeHistory({utaState, business, ...query}) {
  if (utaState === HistoryQueryUtaState.afterUpgrade) {
    return await loadMarginTradeHistory({
      utaState,
      business,
      ...query,
    });
  }
  const {data, error} = await downloadApi.getSpotTrades(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadSpotOrderHistory({utaState, business, ...query}) {
  if (utaState === HistoryQueryUtaState.afterUpgrade) {
    return await loadMarginOrderHistory({
      utaState,
      business,
      ...query,
    });
  }
  const {data, error} = await downloadApi.getSpotOrder(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadContractTradeHistory({utaState, ...query}, isPerpetualV2) {
  // 升级前后记录区分
  const query2 = formatterUpgradedBeforeAfter(
    utaState,
    query,
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: min(unifiedAccountUpgradedTime, p.start),
      end: min(unifiedAccountUpgradedTime, p.end),
    }),
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: max(unifiedAccountUpgradedTime, p.start),
      end: max(unifiedAccountUpgradedTime, p.end),
    })
  );

  const {data, error} = isPerpetualV2 ? await downloadApi.getContractTradeV2(query2) : await downloadApi.getContractTrade(query2);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadContractOrderHistory({utaState, ...query}, isPerpetualV2) {
  // 升级前后记录区分
  const query2 = formatterUpgradedBeforeAfter(
    utaState,
    query,
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: min(unifiedAccountUpgradedTime, p.start),
      end: min(unifiedAccountUpgradedTime, p.end),
    }),
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: max(unifiedAccountUpgradedTime, p.start),
      end: max(unifiedAccountUpgradedTime, p.end),
    })
  );

  const {data, error} = isPerpetualV2 ? await downloadApi.getContractOrderV2(query2) : await downloadApi.getContractOrder(query2);

  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadWalletDetails(query) {
  const {data, error} = await downloadApi.getWalletDetails(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadDeposit(query) {
  const {data, error} = await downloadApi.getDeposit(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadWithdraw(query) {
  const {data, error} = await downloadApi.getWithdraw(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadInvestmentAccount(query) {
  query.withCount = true;
  const {data, error} = await investmentApi.getAccountFlow(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadClosedPNL({offset, limit, dateType, start, end}) {
  // 导出要求全量，不区分币种，v2接口支持老数据
  const query = {
    offset,
    limit,
    dateType,
    start,
    end,
    withCount: true,
  };
  const {data, error} = await assetsApi.closedPositionListV2(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadConversionHistory({offset, limit, start, end, dateType, utaState = HistoryQueryUtaState.beforeUpgrade}) {
  const query = {
    offset,
    limit,
    dateType,
    startTime: start,
    endTime: end,
    withCount: true,
  };
  // 升级前后记录区分
  const query2 = formatterUpgradedBeforeAfter(
    utaState,
    query,
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      startTime: min(unifiedAccountUpgradedTime, p.startTime),
      endTime: min(unifiedAccountUpgradedTime, p.endTime),
    }),
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      startTime: max(unifiedAccountUpgradedTime, p.startTime),
      endTime: max(unifiedAccountUpgradedTime, p.endTime),
    })
  );
  const {data, error} = await assetsApi.getConversionsList(query2);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

function createLoadContractTradeAccountsHistory(utaState = 0) {
  return async function (query) {
    query.withCount = true;

    // 升级前后记录区分
    const query2 = formatterUpgradedBeforeAfter(
      utaState,
      query,
      (p, unifiedAccountUpgradedTime) => ({
        ...p,
        start: min(unifiedAccountUpgradedTime, p.start),
        end: min(unifiedAccountUpgradedTime, p.end),
      }),
      (p, unifiedAccountUpgradedTime) => ({
        ...p,
        start: max(unifiedAccountUpgradedTime, p.start),
        end: max(unifiedAccountUpgradedTime, p.end),
      })
    );

    const {data, error} = await assetsApi.tradeAccountDetailV2(query2);
    if (data) {
      return data;
    }
    if (error) {
      mc.send('toast', 'error', error);
    }
    return {total: 0, rows: []};
  };
}

async function loadContractRepayHistory(query) {
  const {currency, end, start, limit, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
  };
  const {data, error} = await assetsApi.getContractRepayHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadContractBorrowHistory(query) {
  const {currency, end, start, limit, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
  };
  const {data, error} = await assetsApi.getContractBorrowHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadContractInterestHistory(query) {
  const {currency, end, start, limit, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
  };
  const {data, error} = await assetsApi.getContractInterestHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadLaunchPoolHistory(query) {
  query.withCount = true;
  const {data, error} = await launchpoolApi.getLaunchPoolFunds(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadMarginTradeInterestHistory(query) {
  const {currency, end, start, limit, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
  };
  const {data, error} = await marginTradeApi.getBorrowInterestHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadMarginTradeBorrowHistory(query) {
  const {currency, end, start, limit, offset} = query;
  const pageNum = Math.floor(offset / limit) + 1;
  const newQuery = {
    currency,
    pageNum,
    pageSize: limit,
    end,
    start,
  };
  const {data, error} = await marginTradeApi.getBorrowHistory(newQuery);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

function genSpotTradeExportList(rows, {spots, currencies}) {
  if (spots.length < 1) {
    return null;
  }

  return rows.map((item) => {
    const {execPriceEp, side, time, execBaseQtyEv, execQuoteQtyEv, priceEP, quoteQtyEv, baseQtyEv, feeRateEr, ordType, execId, qtyType, execFeeEv, feeCurrency} = item;
    const spot = getSpot(spots, item);
    if (!spot) {
      return [time, '--', side, '--', '--', '--', '--', '--', '--', '--', ordType, execId.substring(0, 8)];
    }
    const {displaySymbol, pricePrecision, displayBaseCurrency, baseQtyPrecision, baseQtyScale, quoteCurrency, quoteQtyPrecision, quoteQtyScale} = spot;
    const amountReceived =
      side === 'Buy'
        ? numUtil.delimit(numUtil.floor(execBaseQtyEv, baseQtyPrecision, baseQtyScale)) + ' ' + displayBaseCurrency
        : numUtil.delimit(numUtil.floor(execQuoteQtyEv, quoteQtyPrecision, quoteQtyScale)) + ' ' + quoteCurrency;
    const value = numUtil.floor(execPriceEp, pricePrecision, quoteQtyScale);
    const amountSpent =
      side === 'Buy'
        ? numUtil.delimit(numUtil.floor(execQuoteQtyEv, quoteQtyPrecision, quoteQtyScale)) + ' ' + quoteCurrency
        : numUtil.delimit(numUtil.floor(execBaseQtyEv, baseQtyPrecision, baseQtyScale)) + ' ' + displayBaseCurrency;
    const amount =
      qtyType === 'ByBase'
        ? numUtil.delimit(numUtil.floor(baseQtyEv, baseQtyPrecision, baseQtyScale)) + ' ' + displayBaseCurrency
        : numUtil.delimit(numUtil.floor(quoteQtyEv, quoteQtyPrecision, quoteQtyScale)) + ' ' + quoteCurrency;
    const price = ordType === 'Market' ? 'Market' : numUtil.floor(priceEP, pricePrecision, quoteQtyScale);
    const curCurrency = getCurrency(currencies, feeCurrency);
    if (!curCurrency) {
      return [time, displaySymbol, side, amountReceived, value, amountSpent, amount, price, '--', '--', ordType, execId.substring(0, 8)];
    }
    const {valueScale, valuePrecision, displayCurrency} = curCurrency;
    const feeRate = numUtil.trimEnd0(numUtil.floor(feeRateEr, 6, 6)) + '%';
    const feePaid = numUtil.floor(execFeeEv, valuePrecision, valueScale) + ' ' + displayCurrency;
    return [time, displaySymbol, side, amountReceived, value, amountSpent, amount, price, feeRate, feePaid, ordType, execId.substring(0, 8)];
  });
}

function genSpotOrderExportList(rows, {spots, currencies}) {
  if (spots.length < 1) {
    return null;
  }

  return rows.map((item) => {
    const {time, side, priceEp, stopPxEp, quoteQtyEv, baseQtyEv, qtyType, avgPriceEp, leavesQuoteQtyEv, leavesBaseQtyEv, cumBaseValueEv, cumQuoteValueEv, ordType, ordStatus, orderID} = item;

    const spot = getSpot(spots, item);
    if (!spot) {
      return [time, '--', side, '--', '--', '--', '--', '--', '--', '--', ordType, ordStatus, orderID];
    }

    const {displaySymbol, pricePrecision, displayBaseCurrency, baseQtyPrecision, baseQtyScale, quoteCurrency, quoteQtyPrecision, quoteQtyScale} = spot;
    const isMarket = ['Market', 'Stop', 'MarketIfTouched'].indexOf(ordType) > -1;
    const isStop = ['Stop', 'StopLimit', 'LimitIfTouched', 'MarketIfTouched'].indexOf(ordType) < 0;
    const amount =
      qtyType === 'ByBase' ? numUtil.floor(baseQtyEv, baseQtyPrecision, baseQtyScale) + ' ' + displayBaseCurrency : numUtil.floor(quoteQtyEv, quoteQtyPrecision, quoteQtyScale) + ' ' + quoteCurrency;
    const orderPrice = isMarket ? 'Market' : numUtil.floor(priceEp, pricePrecision, quoteQtyScale);
    const fillPrice = avgPriceEp === 0 ? '-' : numUtil.floor(avgPriceEp, pricePrecision, quoteQtyScale);
    const filled = qtyType === 'ByBase' ? numUtil.floor(cumBaseValueEv, baseQtyPrecision, baseQtyScale) : numUtil.floor(cumQuoteValueEv, quoteQtyPrecision, baseQtyScale);
    const remaining = qtyType === 'ByBase' ? numUtil.floor(leavesBaseQtyEv, baseQtyPrecision, baseQtyScale) : numUtil.floor(leavesQuoteQtyEv, quoteQtyPrecision, baseQtyScale);
    const finalValue = isMarket
      ? '-'
      : calcValueWithUnit({qtyType, priceEp, baseQtyEv, quoteQtyEv, baseQtyScale, quoteQtyScale, baseQtyPrecision, quoteQtyPrecision, baseCurrency: displayBaseCurrency, quoteCurrency});
    const triggerPrice = isStop ? '-' : numUtil.floor(stopPxEp, pricePrecision, quoteQtyScale);
    return [time, displaySymbol, side, amount, orderPrice, fillPrice, filled + ' / ' + remaining, finalValue, triggerPrice, ordType, ordStatus, orderID];
  });
}

function genContractOrderExportList(rows, {contracts, currencies, lang, LG}) {
  if (contracts.length < 1) {
    return null;
  }
  if (currencies.length < 1) {
    return null;
  }

  const orders = convertOrdersHistory({rows, contracts, currencies});
  return orders.map((order) => {
    return [
      order.createdTime,
      order.symbol,
      order.orderQty,
      genOrderDirection(contracts, order, LG, lang),
      order.orderPrice,
      order.execQty + '/' + order.leavesQty,
      order.orderValue,
      order.triggerPrice,
      order.orderType,
      order.status,
      order.orderId,
    ];
  });
}

function genContractTradeExportList(rows, {contracts, currencies, lang, LG}) {
  if (contracts.length < 1) {
    return null;
  }
  if (currencies.length < 1) {
    return null;
  }

  const orders = convertTradeHistory({rows, contracts, currencies});
  return orders.map((order) => {
    return [
      order.createdTime,
      order.symbol,
      order.execType,
      order.execQty,
      genOrderDirection(contracts, order, LG, lang),
      order.execPrice,
      order.orderQty,
      order.orderPrice,
      order.execValue,
      order.feeRate,
      order.feePaid,
      order.type,
      order.execId,
    ];
  });
}

function genWalletExportList(rows, {currencies, LG}) {
  if (currencies.length < 1) {
    return null;
  }
  return rows.map((item) => {
    const {time, currencyCode, bizCode, amountEv, balanceEv, side} = item;
    const curCurrency = getCurrency(currencies, currencyCode);
    if (!curCurrency) {
      return [time, currencyCode, WALLET_TYPE(LG)[bizCode], '--', '--'];
    }
    const {valueScale, valuePrecision} = curCurrency;
    const ChangeAmount = (side === 0 ? '-' : '+') + numUtil.floor(amountEv, valuePrecision, valueScale) + ' ' + currencyCode;
    const balance = numUtil.floor(balanceEv, valuePrecision, valueScale) + ' ' + currencyCode;
    return [time, currencyCode, WALLET_TYPE(LG)[bizCode], ChangeAmount, balance];
  });
}

function genDepositExportList(rows, {currencies}) {
  if (currencies.length < 1) {
    return null;
  }
  return rows.map((item) => {
    const {time, txHash, amountEv, currency, status} = item;
    const curCurrency = getCurrency(currencies, currency);
    if (!curCurrency) {
      return [time, txHash, '--' + ' ' + currency, status];
    }
    const {valuePrecision, valueScale, displayCurrency} = curCurrency;
    return [time, txHash, numUtil.floor(amountEv, valuePrecision, valueScale) + ' ' + displayCurrency, status];
  });
}

function genWithdrawalExportList(rows, {currencies}) {
  if (currencies.length < 1) {
    return null;
  }
  return rows.map((item) => {
    const {time, address, txHash, amountEv, currency, feeEv, status, rejectReason} = item;
    const curCurrency = getCurrency(currencies, currency);
    if (!curCurrency) {
      return [time, address, txHash || '--', '--', '--', '--'];
    }
    const {valueScale, displayCurrency} = curCurrency;
    const amount = numUtil.floor(amountEv, valueScale, valueScale) + ' ' + displayCurrency;
    const fee = numUtil.floor(feeEv, valueScale, valueScale) + ' ' + displayCurrency;
    const statusData = status + (rejectReason && rejectReason !== null ? '(' + rejectReason + ')' : '');
    return [time, address, txHash || '--', amount, fee, statusData];
  });
}

function genInvestmentAccount(rows, {currencies, LG}) {
  if (currencies.length < 1) {
    return null;
  }
  return rows.map((item) => {
    const {createTime, currency, eventType, updateAmountEv, balanceEv} = item;
    const curCurrency = getCurrency(currencies, currency);
    if (!curCurrency) {
      return [timeUtil.formatUTCDateTime(createTime), currency, INVESTMENT_TYPE(LG)[eventType], '--', '--' + currency];
    }
    const {valuePrecision, valueScale} = getCurrency(currencies, currency);
    return [
      timeUtil.formatUTCDateTime(createTime),
      currency,
      INVESTMENT_TYPE(LG)[eventType],
      (EVENT_TYPE_LIST.indexOf(eventType) > -1 ? '- ' : '+ ') + numUtil.floor(updateAmountEv, valuePrecision, valueScale) + currency,
      numUtil.floor(balanceEv, valuePrecision, valueScale) + currency,
    ];
  });
}

function genClosedPNL(rows, {contracts, currencies}) {
  if (contracts.length < 1) {
    return null;
  }

  const contractsSymbolMap = array2Map(contracts, 'symbol');
  const {rows: res} = convertClosePositionExport([rows, contractsSymbolMap, currencies]);

  return res.map((e) => [e.openTime, e.closeTime, e.symbol, e.openPrice, e.closePrice, e.totalSize, e.closedPnl, e.tradingFeePaid, e.fundingFeePaid, e.realisedPnl, e.roi]);
}

function genConversionHistory(rows, {currencies, LG}) {
  if (currencies.length < 1) {
    return null;
  }
  return rows.map((item) => {
    const {createTime, fromCurrency, conversionRate, toCurrency, status, linkKey} = item;
    return [
      timeUtil.formatUTCDateTime(createTime),
      fromCurrency,
      genPaymentAmount(currencies, item),
      numUtil.floor(conversionRate, 4, 8),
      toCurrency,
      genReceiveAmount(currencies, item),
      statusList(LG)[status],
      linkKey.substring(0, 8),
    ];
  });
}

function genPaymentAmount(currencies, row) {
  const {fromCurrency, fromAmountEv} = row;
  const curCurrency = getCurrency(currencies, fromCurrency);
  if (!curCurrency) {
    return '--';
  }
  const {valueScale, qtyPrecision} = curCurrency;
  return numUtil.floor(fromAmountEv, qtyPrecision, valueScale);
}

function genReceiveAmount(currencies, row) {
  const {toCurrency, toAmountEv} = row;
  const curCurrency = getCurrency(currencies, toCurrency);
  if (!curCurrency) {
    return '--';
  }
  const {valueScale, qtyPrecision} = curCurrency;
  return numUtil.floor(toAmountEv, qtyPrecision, valueScale);
}

function genContractTradeAccountsHistory(rows, {LG}) {
  const data = convertAccountFunds(rows);
  return data.map((item) => [item.createdTime, item.account, TRADE_TYPE(LG)[item.type], item.changeAmount, item.balance]);
}

function genContractRepayHistory(rows, {currencies}) {
  return rows.map((o) => {
    const curCurrency = getCurrency(currencies, o.currency);
    const valuePrecision = curCurrency ? curCurrency.valuePrecision : 8;
    const totalRepayAmountNum = Number(o.principalAmountRv) + Number(o.interestAmountRv);
    const principalAmount = numUtil.floor(o.principalAmountRv, valuePrecision);
    const interestAmount = numUtil.floor(o.interestAmountRv, valuePrecision);
    const totalRepayAmount = numUtil.floor(totalRepayAmountNum, valuePrecision);
    const dateTime = timeUtil.formatDateTime(o.repayTime);
    return [o.currency, dateTime, principalAmount, interestAmount, totalRepayAmount, ContractRepayTypeMap[o.type + ''], o.status];
  });
}

function genContractBorrowHistory(rows, {currencies}) {
  return rows.map((o) => {
    const curCurrency = getCurrency(currencies, o.currency);
    const valuePrecision = curCurrency ? curCurrency.valuePrecision : 8;
    const amount = numUtil.floor(o.amountRv, valuePrecision, 0);
    return [o.currency, timeUtil.formatDateTime(o.borrowTime), amount, o.status];
  });
}

function genContractInterestHistory(rows) {
  return rows.map((o) => {
    const hourlyInterest = numUtil.delimit(numUtil.floor(o.hourlyInterestRv, InterestValuePrecision));
    const hourlyRate = Big(o.hourlyRateRr).times(100).round(HourlyInterestRatePrecision, 3).toFixed(HourlyInterestRatePrecision) + '%';
    const annualRate = Big(o.annualRateRr).times(100).round(AnnualInterestRatePrecision, 3).toFixed(AnnualInterestRatePrecision) + '%';
    return [o.borrowCurrency, timeUtil.formatDateTime(o.interestCalcTime), o.interestCurrency, hourlyInterest, hourlyRate + '/' + annualRate];
  });
}

function genLaunchPoolHistory(rows, {currencies, LG}) {
  if (currencies.length < 1) {
    return null;
  }
  return rows.map((item) => {
    const {investTime, investCurrency, tradeType} = item;
    return [timeUtil.formatDateTime(investTime), investCurrency, LAUNCHPOOL_TYPE(LG)[tradeType], genPoolChangeAmount(currencies, item), genPoolAmount(currencies, item)];
  });
}

function genMarginTradeInterestHistory(rows) {
  return rows.map((o) => {
    const hourlyInterest = numUtil.delimit(numUtil.floor(o.hourlyInterestRv, InterestValuePrecision));
    const hourlyRate = Big(o.hourlyRateRr).times(100).round(HourlyInterestRatePrecision, 3).toFixed(HourlyInterestRatePrecision) + '%';
    const annualRate = Big(o.annualRateRr).times(100).round(AnnualInterestRatePrecision, 3).toFixed(AnnualInterestRatePrecision) + '%';
    return [o.borrowCurrency, timeUtil.formatDateTime(o.interestCalcTime), o.interestCurrency, hourlyInterest, hourlyRate + '/' + annualRate];
  });
}

function createGenMarginOrderExportList(isSpot = false) {
  return function (rows, {spots, currencies}) {
    if (spots.length < 1) {
      return null;
    }

    return rows.map((item) => {
      const {
        createTimeNs,
        side,
        priceEp,
        qtyType,
        avgPriceEp,
        ordType,
        ordStatus,
        orderId,
        feeCurrency,
        priceRp,
        avgPriceRp,
        cumBaseValueRv,
        cumQuoteValueRv,
        leavesBaseQtyRq,
        leavesQuoteQtyRq,
        baseQtyRq,
        quoteQtyRq,
        stopPxRp,
        cumFeeRv,
      } = item;

      const time = timeUtil.formatUTCDateTime(createTimeNs * 1e-6);
      const spot = getSpot(spots, item);
      if (!spot) {
        return [time, '--', side, '--', '--', '--', '--', '--', '--', '--', ordType, ordStatus, orderId];
      }

      const {displaySymbol, pricePrecision, displayBaseCurrency, baseQtyPrecision, quoteCurrency, quoteQtyPrecision} = spot;
      const isMarket = ['Market', 'Stop', 'MarketIfTouched', 'LiqTrade'].indexOf(ordType) > -1;
      const isStop = ['Stop', 'StopLimit', 'LimitIfTouched', 'MarketIfTouched'].indexOf(ordType) < 0;
      const amount = qtyType === 'ByBase' ? numUtil.floor(baseQtyRq, baseQtyPrecision) + ' ' + displayBaseCurrency : numUtil.floor(quoteQtyRq, quoteQtyPrecision) + ' ' + quoteCurrency;
      const orderPrice = isMarket ? 'Market' : numUtil.floor(priceRp, pricePrecision);
      const fillPrice = Number(avgPriceRp) === 0 ? '-' : numUtil.floor(avgPriceRp, pricePrecision);
      const filled = qtyType === 'ByBase' ? numUtil.floor(cumBaseValueRv, baseQtyPrecision) : numUtil.floor(cumQuoteValueRv, quoteQtyPrecision);
      const remaining = qtyType === 'ByBase' ? numUtil.floor(leavesBaseQtyRq, baseQtyPrecision) : numUtil.floor(leavesQuoteQtyRq, quoteQtyPrecision);
      const finalValue = isMarket
        ? '-'
        : calcMarginValueWithUnit({qtyType, baseQtyRq, quoteQtyRq, priceRp, priceEp, baseQtyPrecision, quoteQtyPrecision, baseCurrency: displayBaseCurrency, quoteCurrency});
      const triggerPrice = isStop ? '-' : numUtil.floor(stopPxRp, pricePrecision);
      const {displayCurrency} = currencies.find((o) => o.currency === feeCurrency);
      const feePaid = cumFeeRv + ' ' + displayCurrency;
      if (isSpot) {
        return [time, displaySymbol, side, amount, orderPrice, fillPrice, filled + ' / ' + remaining, finalValue, triggerPrice, ordType, ordStatus, orderId];
      }
      return [time, displaySymbol, side, amount, orderPrice, fillPrice, filled + ' / ' + remaining, finalValue, triggerPrice, feePaid, ordType, ordStatus, orderId];
    });
  };
}

function genPoolChangeAmount(currencies, row) {
  const {requestAmountEv, investCurrency} = row;
  const curCurrency = getCurrency(currencies, investCurrency);
  if (!curCurrency) {
    return '--';
  }
  const {valueScale, valuePrecision} = curCurrency;
  return numUtil.delimit(numUtil.floor(requestAmountEv, valuePrecision, valueScale)) + ' ' + investCurrency;
}

function genPoolAmount(currencies, row) {
  const {balanceEv, investCurrency} = row;
  const curCurrency = getCurrency(currencies, investCurrency);
  if (!curCurrency) {
    return '--';
  }
  const {valueScale, valuePrecision} = curCurrency;
  return numUtil.delimit(numUtil.floor(balanceEv, valuePrecision, valueScale)) + ' ' + investCurrency;
}

function statusList(LG) {
  return {
    1: LG(lang.Processing),
    2: LG(lang.Complated),
    3: LG(lang.Failed),
  };
}

function calcMarginValueWithUnit({qtyType, baseQtyRq, quoteQtyRq, priceRp, baseQtyPrecision, quoteQtyPrecision, baseCurrency, quoteCurrency}) {
  if (qtyType === 'ByBase') {
    const value = Big(baseQtyRq).times(Big(priceRp));
    return [numUtil.floor(Number(value), quoteQtyPrecision), quoteCurrency].join(' ');
  }
  if (qtyType === 'ByQuote') {
    const value = Big(quoteQtyRq).div(Big(priceRp));
    return [numUtil.floor(Number(value), baseQtyPrecision), baseCurrency].join(' ');
  }
  return '-';
}

function calcValueWithUnit({qtyType, priceEp, baseQtyEv, quoteQtyEv, baseQtyScale, quoteQtyScale, baseQtyPrecision, quoteQtyPrecision, baseCurrency, quoteCurrency}) {
  const value = calcValue({qtyType, priceEp, baseQtyEv, quoteQtyEv, baseQtyScale, quoteQtyScale});
  if (qtyType === 'ByBase') {
    return [numUtil.floor(Number(value), quoteQtyPrecision), quoteCurrency].join(' ');
  }
  if (qtyType === 'ByQuote') {
    return [numUtil.floor(Number(value), baseQtyPrecision), baseCurrency].join(' ');
  }
  return '-';
}

function calcValue({qtyType, priceEp, baseQtyEv, quoteQtyEv, baseQtyScale, quoteQtyScale}) {
  if (priceEp === 0) {
    return 0;
  }
  const baseQtyFactor = Big(10).pow(baseQtyScale);
  const quoteQtyFactor = Big(10).pow(quoteQtyScale);
  const priceFactor = Big(10).pow(quoteQtyScale);

  const price = Big(priceEp).div(priceFactor);
  if (qtyType === 'ByBase') {
    const baseQty = Big(baseQtyEv).div(baseQtyFactor);
    return baseQty.times(price);
  }
  if (qtyType === 'ByQuote') {
    const quoteQty = Big(quoteQtyEv).div(quoteQtyFactor);
    return quoteQty.div(price);
  }
  return 0;
}

function downloadWorkBook([rows, context]) {
  const aoaData = [context.headers, ...rows];
  return exportExcelFromAoaData(aoaData, context.fileName + '.csv', context.fileName);
}
